import React, { useState, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { 
  Printer, 
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Mail,
  Trash2,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import VoiceInput from './VoiceInput';

// Local storage key
const PLANNER_KEY = 'calm_keeper_weekly_planner';
const NOTES_KEY = 'calm_keeper_planner_notes';

const getStoredPlanner = () => {
  const stored = localStorage.getItem(PLANNER_KEY);
  return stored ? JSON.parse(stored) : {};
};

const savePlanner = (data) => {
  localStorage.setItem(PLANNER_KEY, JSON.stringify(data));
};

const getStoredNotes = () => {
  const stored = localStorage.getItem(NOTES_KEY);
  return stored ? JSON.parse(stored) : {};
};

const saveNotes = (data) => {
  localStorage.setItem(NOTES_KEY, JSON.stringify(data));
};

const formatDate = (date) => {
  return date.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric' 
  });
};

const getWeekRange = (date) => {
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay());
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

const WeeklyPlanner = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [plannerData, setPlannerData] = useState(getStoredPlanner());
  const [notesData, setNotesData] = useState(getStoredNotes());
  const [newTask, setNewTask] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const printRef = useRef();
  
  const dateKey = currentDate.toISOString().split('T')[0];
  const dayTasks = plannerData[dateKey] || [];
  const dayNotes = notesData[dateKey] || '';
  const isToday = new Date().toDateString() === currentDate.toDateString();
  
  const goToPrevDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };
  
  const goToNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };
  
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  
  // Get week overview for mini-calendar
  const getWeekOverview = () => {
    const overview = [];
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      const key = date.toISOString().split('T')[0];
      const tasks = plannerData[key] || [];
      const taskCount = tasks.length;
      const completedCount = tasks.filter(t => t.done).length;
      overview.push({
        date,
        key,
        taskCount,
        completedCount,
        isSelected: key === dateKey,
        isToday: new Date().toDateString() === date.toDateString(),
      });
    }
    return overview;
  };
  
  const weekOverview = getWeekOverview();
  
  const handleAddTask = () => {
    if (newTask.trim()) {
      const newData = { ...plannerData };
      if (!newData[dateKey]) {
        newData[dateKey] = [];
      }
      newData[dateKey].push({ text: newTask.trim(), done: false });
      setPlannerData(newData);
      savePlanner(newData);
      setNewTask('');
      toast.success('Task added!');
    }
  };
  
  const handleToggleTask = (taskIdx) => {
    const newData = { ...plannerData };
    if (newData[dateKey] && newData[dateKey][taskIdx]) {
      newData[dateKey][taskIdx].done = !newData[dateKey][taskIdx].done;
      setPlannerData(newData);
      savePlanner(newData);
    }
  };
  
  const handleRemoveTask = (taskIdx) => {
    const newData = { ...plannerData };
    if (newData[dateKey]) {
      newData[dateKey].splice(taskIdx, 1);
      setPlannerData(newData);
      savePlanner(newData);
      toast.success('Task removed');
    }
  };
  
  const handleNotesChange = (value) => {
    const newData = { ...notesData };
    newData[dateKey] = value;
    setNotesData(newData);
    setHasUnsavedChanges(true);
  };
  
  const handleSaveNotes = () => {
    saveNotes(notesData);
    setHasUnsavedChanges(false);
    toast.success('Notes saved!');
  };
  
  const handlePrint = () => {
    window.print();
    toast.success('Opening print dialog...');
  };
  
  const handleEmailPlanner = () => {
    // Get current week's dates
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    
    let emailBody = `Weekly Planner: ${getWeekRange(currentDate)}\n\n`;
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      const key = date.toISOString().split('T')[0];
      const tasks = plannerData[key] || [];
      const notes = notesData[key] || '';
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
      
      emailBody += `📅 ${dayName}\n`;
      if (tasks.length > 0) {
        tasks.forEach((task) => {
          emailBody += `  ${task.done ? '✅' : '◻️'} ${task.text}\n`;
        });
      } else {
        emailBody += `  (No tasks planned)\n`;
      }
      if (notes) {
        emailBody += `  📝 Notes: ${notes}\n`;
      }
      emailBody += '\n';
    }
    
    emailBody += '\n---\nSent from The Calm Keeper Compass 🌿\n"You are doing enough."';
    
    const subject = `Weekly Planner: ${getWeekRange(currentDate)}`;
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;
    
    window.open(mailtoLink, '_self');
    toast.success('Opening email client...');
  };
  
  return (
    <div className="space-y-6" data-testid="weekly-planner-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-serif text-2xl md:text-3xl text-ink-900 mb-2">
            Daily Planner
          </h2>
          <p className="text-ink-500 font-accent italic">
            Plan your day, one task at a time 🌿
          </p>
        </div>
        <div className="w-12 h-12 rounded-full bg-sage/10 flex items-center justify-center border border-sage/20">
          <Calendar className="w-6 h-6 text-sage" />
        </div>
      </div>
      
      {/* Week Mini-Overview */}
      <Card className="p-4 bg-paper-light shadow-lifted border border-stone-200">
        <div className="flex justify-between items-center mb-3">
          <p className="text-xs text-ink-500 font-accent">This Week</p>
          <Button
            onClick={goToToday}
            variant="ghost"
            size="sm"
            className="text-xs text-sage hover:text-sage-dark h-6"
          >
            Today
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {weekOverview.map((day) => (
            <button
              key={day.key}
              onClick={() => setCurrentDate(day.date)}
              className={`
                p-2 rounded-lg text-center transition-all
                ${day.isSelected 
                  ? 'bg-sage text-white' 
                  : day.isToday 
                    ? 'bg-sage/10 text-sage' 
                    : 'hover:bg-stone-100'
                }
              `}
            >
              <p className="text-xs font-accent">
                {day.date.toLocaleDateString('en-US', { weekday: 'short' }).charAt(0)}
              </p>
              <p className={`text-sm font-medium ${day.isSelected ? 'text-white' : ''}`}>
                {day.date.getDate()}
              </p>
              <div className="flex justify-center gap-0.5 mt-1">
                {day.taskCount > 0 ? (
                  <div 
                    className={`w-2 h-2 rounded-full ${
                      day.completedCount === day.taskCount 
                        ? day.isSelected ? 'bg-white' : 'bg-green-500'
                        : day.isSelected ? 'bg-white/60' : 'bg-sage/60'
                    }`}
                  />
                ) : (
                  <div className={`w-2 h-2 rounded-full ${day.isSelected ? 'bg-white/30' : 'bg-stone-200'}`} />
                )}
              </div>
            </button>
          ))}
        </div>
      </Card>
      
      {/* Day Navigation */}
      <div className="flex items-center justify-between">
        <Button
          onClick={goToPrevDay}
          variant="ghost"
          size="icon"
          className="text-ink-500 hover:text-sage"
          data-testid="planner-prev-day"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        
        <div className="text-center">
          <h3 className={`font-serif text-xl ${isToday ? 'text-sage' : 'text-ink-900'}`}>
            {formatDate(currentDate)}
          </h3>
          {isToday && (
            <span className="text-xs text-sage font-accent">Today</span>
          )}
        </div>
        
        <Button
          onClick={goToNextDay}
          variant="ghost"
          size="icon"
          className="text-ink-500 hover:text-sage"
          data-testid="planner-next-day"
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>
      
      {/* Day Tasks - Full Width */}
      <div ref={printRef} className="print-container">
        <Card className="p-5 bg-white shadow-lifted border border-stone-200">
          {/* Print Header */}
          <div className="hidden print:block text-center mb-4">
            <h1 className="font-serif text-2xl text-ink-900">The Calm Keeper Compass</h1>
            <p className="text-ink-500 font-accent italic">Daily Planner - {formatDate(currentDate)}</p>
          </div>
          
          {/* Add Task Input */}
          <div className="flex gap-2 mb-4">
            <Input
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddTask()}
              placeholder="Add a new task..."
              className="flex-1 border-stone-300 font-handwritten text-lg"
              data-testid="planner-new-task-input"
            />
            <Button
              onClick={handleAddTask}
              className="vintage-btn btn-sage rounded-lg"
              data-testid="planner-add-task-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
          
          {/* Tasks List */}
          <div className="space-y-3 min-h-[200px]">
            {dayTasks.length === 0 ? (
              <div className="text-center py-8 text-ink-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-accent italic">No tasks for this day yet.</p>
                <p className="text-xs mt-1">Add your first task above!</p>
              </div>
            ) : (
              dayTasks.map((task, idx) => (
                <div 
                  key={idx} 
                  className={`
                    flex items-center gap-3 p-3 rounded-lg border transition-all group
                    ${task.done 
                      ? 'bg-sage/5 border-sage/20' 
                      : 'bg-paper-light border-stone-200 hover:border-sage/30'
                    }
                  `}
                >
                  <Checkbox
                    checked={task.done}
                    onCheckedChange={() => handleToggleTask(idx)}
                    className="h-5 w-5"
                    data-testid={`planner-task-checkbox-${idx}`}
                  />
                  <span className={`
                    flex-1 font-handwritten text-lg
                    ${task.done ? 'line-through text-ink-500' : 'text-ink-900'}
                  `}>
                    {task.text}
                  </span>
                  <button
                    onClick={() => handleRemoveTask(idx)}
                    className="opacity-0 group-hover:opacity-100 text-ink-500 hover:text-red-500 transition-opacity p-1"
                    data-testid={`planner-task-delete-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
          
          {/* Daily Notes */}
          <div className="mt-6 pt-4 border-t border-dashed border-stone-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-ink-500 font-accent italic">Daily Notes & Reminders:</p>
              <VoiceInput onTranscription={(text) => handleNotesChange(dayNotes ? dayNotes + ' ' + text : text)} />
            </div>
            <Textarea
              value={dayNotes}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder="Write any notes for this day..."
              className="min-h-[100px] bg-paper-light border-stone-300 font-handwritten text-lg resize-none"
              data-testid="planner-notes-textarea"
            />
          </div>
          
          {/* Print Footer */}
          <div className="hidden print:block text-center mt-6 text-xs text-ink-500">
            <p className="font-handwritten text-base">You are doing enough. 🌿</p>
          </div>
        </Card>
      </div>
      
      {/* Save Notes Button */}
      {hasUnsavedChanges && (
        <Button
          onClick={handleSaveNotes}
          className="w-full vintage-btn btn-sage rounded-lg animate-fade-in"
          data-testid="planner-save-notes-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          Save Notes
        </Button>
      )}
      
      {/* Action Buttons */}
      <div className="flex gap-3 no-print">
        <Button
          data-testid="planner-print-btn"
          onClick={handlePrint}
          className="flex-1 vintage-btn btn-brown rounded-lg"
        >
          <Printer className="w-4 h-4 mr-2" />
          Print Day
        </Button>
        <Button
          data-testid="planner-email-btn"
          onClick={handleEmailPlanner}
          variant="outline"
          className="flex-1 border-sage text-sage hover:bg-sage-mist"
        >
          <Mail className="w-4 h-4 mr-2" />
          Email Week
        </Button>
      </div>
      
      {/* Tips */}
      <Card className="p-4 bg-warmth-sand border border-stone-300 no-print">
        <p className="text-sm text-ink-700 font-handwritten text-lg text-center">
          🌿 Use the week overview to quickly jump between days!
        </p>
      </Card>
    </div>
  );
};

export default WeeklyPlanner;
